import { useState, useMemo, useRef, type FC } from 'react';
import ReactPlayer from 'react-player';

import styled, { css } from 'styled-components';
import type { HeroBasicProps } from './types';

import {
  defaultWebValue,
  filterBackgroundColor,
  sanitizeRichText,
  COLOR_YELLOW,
  generateAccessKey
} from 'shared/helpers/index';
import { useSales, useWebsiteEditor, useSublineColor } from 'shared/hooks/index';
import { HoverableInput } from '../HoverableInput';

import { Wrapper, Button, Download } from '../Common';
import { Image as ImageComp } from 'shared/components/Common';
import useIsEmpty from 'shared/hooks/useIsEmpty';

const Hero = styled.div`
  position: relative;
  overflow: hidden;
  padding: 70px 0 94px;

  ${({ theme: { $width } }) =>
    $width <= 991 &&
    `
    padding: 70px 0;
  `}

  ${({ theme: { $width } }) =>
    $width <= 575 &&
    `
    padding: 50px 0 70px;
  `}
`;

const StyledWrapper = styled((props) => <Wrapper {...props} />)`
  position: relative;
  z-index: 20;
  display: flex;
  justify-content: space-between;
  align-items: center;

  ${({ theme: { $width } }) =>
    $width <= 768 &&
    `
    flex-direction: column;
  `}
`;

const TitleWrapper = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  text-align: center;
`;

const playerStyles = css`
  width: 100% !important;
  height: 720px !important;
  border: none;
  position: relative;

  ${({ theme: { $width } }) =>
    $width <= 1254 &&
    `
    height: 560px !important;
  `}

  ${({ theme: { $width } }) =>
    $width <= 991 &&
    `
  height: 430px !important;
`}

  ${({ theme: { $width } }) =>
    $width <= 768 &&
    `
    height: 310px !important;
  `}

  & div, & .exampleImg {
    width: 100% !important;
    height: 720px !important;
    border: none;

    ${({ theme: { $width } }) =>
      $width <= 1254 &&
      `
    height: 560px !important;
  `}

    ${({ theme: { $width } }) =>
      $width <= 991 &&
      `
  height: 430px !important;
`}

    ${({ theme: { $width } }) =>
      $width <= 768 &&
      `
    height: 310px !important;
  `}
  }
`;

const Title = styled.h1<{ isEmpty?: boolean; editable?: boolean }>`
  font-size: ${({ theme: { $largeHeadlineFont } }) => $largeHeadlineFont};
  margin-bottom: 26px;
  line-height: 1.35em;
  position: relative;

  ${({ theme: { $width } }) =>
    $width <= 1254 &&
    `
    margin-bottom: 28px;
  `}

  ${({ theme: { $width } }) =>
    $width <= 991 &&
    `
    margin-bottom: 20px;
  `}

  ${({ isEmpty, editable }) =>
    isEmpty
      ? `
    display: ${editable ? 'block' : 'none'};
    white-space: nowrap;
    &:not(:focus):before {
      content: attr(data-placeholder);
      cursor: text;
      color: #aaa;
      position: absolute;
      display: flex;
      justify-content: center;
      align-items: center;
      width: 100%;
    }
  `
      : `
    &:before {
      content: none;
    } 
  `}
`;

const Description = styled.p<{ isEmpty?: boolean; editable?: boolean }>`
  font-size: ${({ theme: { $paragraphFont } }) => $paragraphFont};
  margin-bottom: 45px;
  line-height: 1.35em;
  position: relative;

  ${({ theme: { $width } }) =>
    $width <= 1254 &&
    `
    margin-bottom: 40px;
  `}

  ${({ theme: { $width } }) =>
    $width <= 991 &&
    `
    margin-bottom: 70px;
  `}

  ${({ theme: { $width } }) =>
    $width <= 768 &&
    `
    margin-bottom: 50px;
  `}

  ${({ isEmpty, editable }) =>
    isEmpty
      ? `
    display: ${editable ? 'block' : 'none'};
    white-space:nowrap;
    &:not(:focus):before {
      content: attr(data-placeholder);
      cursor: text;
      color: #aaa;
      position: absolute;
      display: flex;
      justify-content: center;
      align-items: center;
      width: 100%;
    }
  `
      : `
    &:before {
      content: none;
    } 
  `}
`;

const BtnInner = styled.div`
  display: flex;
  justify-content: center;
  padding-top: 70px;

  ${({ theme: { $width } }) =>
    $width <= 768 &&
    `
    width: 100%;
  `}
`;

const buttonStyles = css`
  max-width: 465px;
  display: inline-block;
  padding-left: 50px;
  padding-right: 50px;

  ${({ theme: { $width } }) =>
    $width <= 1254 &&
    `
      max-width: 345px;
    `}

  ${({ theme: { $width } }) =>
    $width <= 768 &&
    `
      max-width: 380px;
      padding: 19px 32px;
    `}
`;

const DownloadStyled = styled(Download)`
  ${buttonStyles}
`;

const ButtonStyled = styled(Button)`
  ${buttonStyles}
`;

const WhiteGradient = styled.div`
  width: 100%;
  height: 100%;
  position: absolute;
  left: 0;
  top: 0;
  z-index: 3;
  background: linear-gradient(180deg, #fff 0%, rgba(255, 255, 255, 0.05) 0.01%, #fff 75.52%);
`;

const DynamicBackground = styled.div`
  width: 100%;
  height: 98%;
  position: absolute;
  left: 0;
  top: 0;
  z-index: 1;
`;

const ReactPlayerStyled = styled(ReactPlayer)`
  width: 100% !important;
`;

export const V5: FC<HeroBasicProps> = (props) => {
  const {
    title = defaultWebValue,
    content = defaultWebValue,
    button = defaultWebValue,
    secondaryColor = COLOR_YELLOW,
    customBackgroundColor,
    textColor,
    meta,
    hidden,
    order
  } = props;

  const {
    updateActiveElement,
    resetActiveElement,
    containerClassname,
    editable,
    websiteAssembler
  } = useWebsiteEditor();
  const { purchaseElementId, type, renderSales, isDashboard } = useSales();

  const [sublineColor, setSublineColor] = useState<string>('#fff');
  const topRef = useRef<HTMLDivElement>(null);

  useSublineColor(topRef, setSublineColor, renderSales);

  const { isEmpty: isTitleEmpty } = useIsEmpty(meta, 'title');
  const { isEmpty: isContentEmpty } = useIsEmpty(meta, 'content');

  const resolvedBackgroundColor = useMemo(() => {
    return customBackgroundColor || secondaryColor;
  }, [customBackgroundColor, secondaryColor]);

  // Accessibility improvements
  const titleAccessKey = generateAccessKey('ht', order);
  const contentAccessKey = generateAccessKey('hc', order);
  const buttonAccessKey = generateAccessKey('hb', order);
  const videoAccessKey = generateAccessKey('hv', order);
  const skipLinkId = `skip-to-hero-${meta['accessorKey'] || 'hero'}`;

  const handleEditError = (error: Error | unknown) => {
    // Announce error to screen readers
    const errorAnnouncement = document.createElement('div');
    errorAnnouncement.setAttribute('aria-live', 'polite');
    errorAnnouncement.setAttribute('aria-atomic', 'true');
    errorAnnouncement.className = 'sr-only';
    errorAnnouncement.textContent = `Hero edit error: ${error instanceof Error ? error.message : 'Edit action failed'}`;
    document.body.appendChild(errorAnnouncement);
    setTimeout(() => document.body.removeChild(errorAnnouncement), 1000);
  };

  const handleModalError = (error: Error | unknown) => {
    // Announce error to screen readers
    const errorAnnouncement = document.createElement('div');
    errorAnnouncement.setAttribute('aria-live', 'polite');
    errorAnnouncement.setAttribute('aria-atomic', 'true');
    errorAnnouncement.className = 'sr-only';
    errorAnnouncement.textContent = `Button action error: ${error instanceof Error ? error.message : 'Action failed'}`;
    document.body.appendChild(errorAnnouncement);
    setTimeout(() => document.body.removeChild(errorAnnouncement), 1000);
  };

  // Determine button type for accessibility
  const buttonType = meta['url']
    ? 'external link'
    : type === 'delivery'
      ? 'delivery action'
      : 'purchase button';

  // Video content accessibility
  const hasVideo = useMemo(() => Boolean(meta['url_1']), [meta]);

  const videoDescription = useMemo(() => {
    return hasVideo
      ? 'Video content about the book or topic'
      : 'Video placeholder - upload a video to showcase your content';
  }, [hasVideo]);

  return (
    <Hero
      ref={topRef}
      className={containerClassname}
      style={{
        display: hidden && !websiteAssembler ? 'none' : 'block',
        opacity: hidden ? 0.3 : 1
      }}
      aria-label="Hero section with centered video and content"
      role="region">
      {/* Skip link for keyboard navigation */}
      <span className="sr-only">
        <a href={`#${skipLinkId}`}>Skip to hero content</a>
      </span>

      <StyledWrapper id={skipLinkId}>
        <TitleWrapper id="HeadlineSection">
          <Title
            id={title.id}
            dangerouslySetInnerHTML={{
              __html: sanitizeRichText(title.val, isDashboard)
            }}
            style={{ color: filterBackgroundColor(resolvedBackgroundColor, textColor) }}
            isEmpty={isTitleEmpty}
            {...(isDashboard
              ? {
                  onClick: (e: React.MouseEvent<HTMLElement>) => {
                    try {
                      updateActiveElement(e);
                    } catch (error) {
                      handleEditError(error);
                    }
                  },
                  onBlur: resetActiveElement,
                  'data-accessor-parent': meta['accessorKey'],
                  'data-accessorkey': 'title',
                  'data-placeholder': '+',
                  editable: true
                }
              : {
                  'aria-label':
                    isTitleEmpty || !title.val?.trim() ? 'Hero section main title' : undefined,
                  accessKey: titleAccessKey,
                  title: `Press Alt+${titleAccessKey} to focus this hero title`,
                  tabIndex: 0
                })}
          />

          <Description
            id={content.id}
            dangerouslySetInnerHTML={{ __html: sanitizeRichText(content.val, editable) }}
            style={{
              color: filterBackgroundColor(resolvedBackgroundColor, textColor)
            }}
            isEmpty={isContentEmpty}
            {...(isDashboard
              ? {
                  onClick: (e: React.MouseEvent<HTMLElement>) => {
                    try {
                      updateActiveElement(e);
                    } catch (error) {
                      handleEditError(error);
                    }
                  },
                  onBlur: resetActiveElement,
                  'data-accessor-parent': meta['accessorKey'],
                  'data-accessorkey': 'content',
                  'data-placeholder': '+',
                  editable: true
                }
              : {
                  role: 'textbox',
                  'aria-multiline': 'true',
                  'aria-label':
                    isContentEmpty || !content.val?.trim() ? 'Hero section description' : undefined,
                  'aria-describedby': title.id,
                  accessKey: contentAccessKey,
                  title: `Press Alt+${contentAccessKey} to focus this hero description`,
                  tabIndex: 0
                })}
          />

          <Wrapper>
            <div
              role="region"
              aria-label={videoDescription}
              aria-describedby={`${title.id} ${content.id}`}
              {...(!isDashboard && {
                accessKey: videoAccessKey,
                title: `Press Alt+${videoAccessKey} to focus the video area`,
                tabIndex: 0
              })}>
              <HoverableInput
                disabled={!editable}
                css={playerStyles as never}
                meta={meta}
                index={1}
                aria-label={
                  editable ? 'Video upload area - drag and drop to add video' : undefined
                }>
                {meta['url_1'] ? (
                  <ReactPlayerStyled
                    url={meta['url_1']}
                    controls={true}
                    aria-label="Video player with content related to the book or topic"
                    role="application"
                  />
                ) : (
                  <ImageComp
                    alt="Video placeholder - shows where video content will appear"
                    className="exampleImg"
                    width={500}
                    height={500}
                    src="/img/example_video.png"
                    role="img"
                    aria-describedby={editable ? 'video-upload-instructions' : undefined}
                  />
                )}
                {editable && !meta['url_1'] && (
                  <span id="video-upload-instructions" className="sr-only">
                    Click or drag to upload a video file for your hero section
                  </span>
                )}
              </HoverableInput>
            </div>
          </Wrapper>

          <BtnInner role="group" aria-label={`Hero action area - ${buttonType}`}>
            {meta['url'] || type === 'delivery' ? (
              <DownloadStyled
                textColor={textColor}
                button={button}
                meta={meta}
                outerOnClick={() => {
                  if (editable) {
                    try {
                      // empty needs only for error
                    } catch (error) {
                      handleModalError(error);
                    }
                  }
                }}
                {...(!isDashboard && {
                  accessKey: buttonAccessKey,
                  title: `Press Alt+${buttonAccessKey} to activate this ${buttonType}`,
                  'aria-describedby': `${title.id} ${content.id}`
                })}
              />
            ) : (
              <ButtonStyled
                to={purchaseElementId}
                button={button}
                meta={meta}
                outerOnClick={() => {
                  if (editable) {
                    try {
                      // empty needs only for error
                    } catch (error) {
                      handleModalError(error);
                    }
                  }
                }}
                {...(!isDashboard && {
                  accessKey: buttonAccessKey,
                  title: `Press Alt+${buttonAccessKey} to activate this ${buttonType}`,
                  'aria-describedby': `${title.id} ${content.id}`
                })}
              />
            )}
          </BtnInner>
        </TitleWrapper>
      </StyledWrapper>

      <WhiteGradient
        style={{
          background: `linear-gradient(180deg, ${sublineColor} 0%, rgba(255, 255, 255, 0.05) 0.01%, ${sublineColor} 75.52%)`
        }}
        aria-hidden="true"
        role="presentation"
      />
      <DynamicBackground
        style={{ background: resolvedBackgroundColor }}
        aria-hidden="true"
        role="presentation"
      />
    </Hero>
  );
};

V5.displayName = 'HeroBasicV5';
